import { ENTERPRISE_ADLE_MODULE_VALUES } from "../../utils/constants";
import { ENTERPRISE_CREATIVES_BUFFER_NONE_VALUE } from "./config/enterpriseIntakeModuleFieldRegistry";
import { enterpriseIntakeDefaultValues } from "./enterpriseIntakeDefaultValues";

export const ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY = "agency";
export const ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND = "brand";

export const ENTERPRISE_INTAKE_SHARED_FIELD_KEYS = [
  "fullName",
  "corporateEmail",
  "companyName",
  "jobTitle",
  "selectedAdleModules",
  "scopeBrandConfigsCount",
  "module1MonthlyNewImagesCount",
  "module1MonthlyImageAnimationsCount",
  "module1MonthlyVideosCount",
  "creativesBufferTypes",
  "creativesBufferPercent",
  "creativesBufferPercentOther",
  "module2MonthlyCampaignsCount",
  "optimizationFrequency",
  "platformUsersCount",
  "contractTermMonths",
  "additionalNotes",
];

const AGENCY_ONLY_DEFAULTS = {
  whiteLabelRequirement: "",
};

const BRAND_ONLY_DEFAULTS = {
  mediaManagementAddon: "",
  monthlyMediaSpendUsd: "",
};

const asTrimmedString = (value) =>
  typeof value === "string" ? value.trim() : "";

const cloneValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item));
  }
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nested]) => {
      acc[key] = cloneValue(nested);
      return acc;
    }, {});
  }
  return value;
};

const pickFields = ({ source = {}, keys = [] }) =>
  keys.reduce((acc, key) => {
    acc[key] = source[key] ?? enterpriseIntakeDefaultValues[key];
    return acc;
  }, {});

export const getEnterpriseIntakeOrganizationType = (raw = {}) => {
  if (raw.organizationType === ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND) {
    return ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND;
  }
  return ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY;
};

export const isEnterpriseIntakeAgency = (organizationType) =>
  organizationType !== ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND;

export const hasSelectedEnterpriseModule = ({ selectedModules, moduleValue }) =>
  Array.isArray(selectedModules) && selectedModules.includes(moduleValue);

export const wantsCreativesBuffer = (creativesBufferTypes) => {
  if (
    !Array.isArray(creativesBufferTypes) ||
    creativesBufferTypes.length === 0
  ) {
    return false;
  }
  return !creativesBufferTypes.includes(ENTERPRISE_CREATIVES_BUFFER_NONE_VALUE);
};

export const applyEnterpriseIntakeConditionalCleanup = (values = {}) => {
  const next = {
    ...cloneValue(enterpriseIntakeDefaultValues),
    ...cloneValue(values),
  };
  const selectedModules = Array.isArray(next.selectedAdleModules)
    ? next.selectedAdleModules
    : [];
  const hasCreativeAssets = hasSelectedEnterpriseModule({
    selectedModules,
    moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.CREATIVE_ASSETS,
  });
  const hasCampaigns =
    hasSelectedEnterpriseModule({
      selectedModules,
      moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_LAUNCH,
    }) ||
    hasSelectedEnterpriseModule({
      selectedModules,
      moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_OPTIMIZATION,
    });
  const hasOptimizations = hasSelectedEnterpriseModule({
    selectedModules,
    moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_OPTIMIZATION,
  });
  const isAgency = isEnterpriseIntakeAgency(next.organizationType);

  if (!hasCreativeAssets) {
    next.module1MonthlyNewImagesCount = "";
    next.module1MonthlyImageAnimationsCount = "";
    next.module1MonthlyVideosCount = "";
    next.creativesBufferTypes = [];
    next.creativesBufferPercent = "";
    next.creativesBufferPercentOther = "";
  } else if (!wantsCreativesBuffer(next.creativesBufferTypes)) {
    next.creativesBufferPercent = "";
    next.creativesBufferPercentOther = "";
  } else if (next.creativesBufferPercent !== "other") {
    next.creativesBufferPercentOther = "";
  }

  if (!hasCampaigns) {
    next.module2MonthlyCampaignsCount = "";
  }

  if (!hasOptimizations) {
    next.optimizationFrequency = "";
  }

  if (isAgency) {
    next.mediaManagementAddon = "";
    next.monthlyMediaSpendUsd = "";
  } else {
    next.whiteLabelRequirement = "";
    if (next.mediaManagementAddon !== "yes") {
      next.monthlyMediaSpendUsd = "";
    }
  }

  return next;
};

export const buildFormValuesForOrganizationType = ({
  organizationType,
  currentValues = {},
}) => {
  const sharedValues = pickFields({
    source: currentValues,
    keys: ENTERPRISE_INTAKE_SHARED_FIELD_KEYS,
  });
  const nextType = getEnterpriseIntakeOrganizationType({ organizationType });

  return applyEnterpriseIntakeConditionalCleanup({
    ...enterpriseIntakeDefaultValues,
    ...sharedValues,
    ...(nextType === ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY
      ? cloneValue(AGENCY_ONLY_DEFAULTS)
      : cloneValue(BRAND_ONLY_DEFAULTS)),
    organizationType: nextType,
  });
};

export const sanitizeEnterpriseIntakeSubmitValues = (values = {}) => {
  const cleaned = applyEnterpriseIntakeConditionalCleanup(values);
  const selectedModules = Array.isArray(cleaned.selectedAdleModules)
    ? cleaned.selectedAdleModules
    : [];
  const hasCreativeAssets = hasSelectedEnterpriseModule({
    selectedModules,
    moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.CREATIVE_ASSETS,
  });
  const hasCampaigns =
    hasSelectedEnterpriseModule({
      selectedModules,
      moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_LAUNCH,
    }) ||
    hasSelectedEnterpriseModule({
      selectedModules,
      moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_OPTIMIZATION,
    });
  const hasOptimizations = hasSelectedEnterpriseModule({
    selectedModules,
    moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_OPTIMIZATION,
  });
  const isAgency = isEnterpriseIntakeAgency(cleaned.organizationType);

  const payload = {
    organizationType: cleaned.organizationType,
    fullName: asTrimmedString(cleaned.fullName),
    corporateEmail: asTrimmedString(cleaned.corporateEmail),
    companyName: asTrimmedString(cleaned.companyName),
    jobTitle: asTrimmedString(cleaned.jobTitle),
    selectedAdleModules: selectedModules,
    scopeBrandConfigsCount: asTrimmedString(cleaned.scopeBrandConfigsCount),
    platformUsersCount: asTrimmedString(cleaned.platformUsersCount),
    contractTermMonths: asTrimmedString(cleaned.contractTermMonths),
    additionalNotes: asTrimmedString(cleaned.additionalNotes),
  };

  if (hasCreativeAssets) {
    payload.module1MonthlyNewImagesCount = asTrimmedString(
      cleaned.module1MonthlyNewImagesCount
    );
    payload.module1MonthlyImageAnimationsCount = asTrimmedString(
      cleaned.module1MonthlyImageAnimationsCount
    );
    payload.module1MonthlyVideosCount = asTrimmedString(
      cleaned.module1MonthlyVideosCount
    );
    payload.creativesBufferTypes = Array.isArray(cleaned.creativesBufferTypes)
      ? cleaned.creativesBufferTypes
      : [];
    if (wantsCreativesBuffer(payload.creativesBufferTypes)) {
      payload.creativesBufferPercent = asTrimmedString(
        cleaned.creativesBufferPercent
      );
      if (payload.creativesBufferPercent === "other") {
        payload.creativesBufferPercentOther = asTrimmedString(
          cleaned.creativesBufferPercentOther
        );
      }
    }
  }

  if (hasCampaigns) {
    payload.module2MonthlyCampaignsCount = asTrimmedString(
      cleaned.module2MonthlyCampaignsCount
    );
  }

  if (hasOptimizations) {
    payload.optimizationFrequency = asTrimmedString(
      cleaned.optimizationFrequency
    );
  }

  if (isAgency) {
    payload.whiteLabelRequirement = asTrimmedString(
      cleaned.whiteLabelRequirement
    );
    return payload;
  }

  payload.mediaManagementAddon = asTrimmedString(cleaned.mediaManagementAddon);
  if (payload.mediaManagementAddon === "yes") {
    payload.monthlyMediaSpendUsd = asTrimmedString(
      cleaned.monthlyMediaSpendUsd
    );
  }

  return payload;
};
